import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DDNav } from "../../../components/Admin/DDNav/DDNav";
import { DDLoadError } from "../../../components/Admin/DDLoadError";
import { ConfirmModal } from "../../../components/Admin/ConfirmModal/ConfirmModal";
import { useAuth } from "../../../hooks";
import { DdOcdeDd6 } from "../../../api";
import {
  DD6_STAGES, DD6_STATES, DD6_STATE_ORDER, DD6_MODULE_LINKS,
  dd6Normalize, dd6StageScore, dd6GlobalScore, dd6Level,
} from "./ocdeDd6Catalog";
import "./OcdeDdProcess.scss";

const ddDd6Api = new DdOcdeDd6();
const SAVE_DELAY_MS = 600;
const MAX_FILE_MB = 10;

export function OcdeDdProcess() {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [state, setState]     = useState(() => ({ data: dd6Normalize({}), current: "s1" }));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [view, setView]       = useState("stages");
  const [uploading, setUploading] = useState({});
  const [confirm, setConfirm] = useState({ open: false, msg: "", onConfirm: null });

  // Guardado diferido: cada cambio reprograma el guardado; al salir de la página se guarda lo pendiente.
  const stateRef = useRef(state);
  const tokenRef = useRef(accessToken);
  const timerRef = useRef(null);
  stateRef.current = state;
  tokenRef.current = accessToken;

  const flush = useCallback(() => {
    timerRef.current = null;
    const s = stateRef.current;
    if (!tokenRef.current) return;
    ddDd6Api.saveData(tokenRef.current, { data: s.data, current: s.current })
      .catch((err) => console.error("Error guardando Proceso DD", err));
  }, []);

  useEffect(() => () => {
    if (timerRef.current) { clearTimeout(timerRef.current); flush(); }
  }, [flush]);

  // Si la carga falla no se muestra el checklist vacío: el primer cambio lo guardaría
  // encima de los datos reales de la empresa.
  useEffect(() => {
    if (!accessToken) return;
    setLoading(true);
    setLoadError(false);
    ddDd6Api.getData(accessToken)
      .then((d) => { if (d) setState({ data: dd6Normalize(d.data), current: d.current || "s1" }); })
      .catch((err) => { console.error("Error cargando Proceso DD", err); setLoadError(true); })
      .finally(() => setLoading(false));
  }, [accessToken, reloadKey]);

  const change = (updater) => {
    const next = updater(stateRef.current);
    stateRef.current = next;
    setState(next);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, SAVE_DELAY_MS);
  };

  const updateReq = (sid, rc, patch) => change((s) => ({
    ...s,
    data: { ...s.data, [sid]: { ...s.data[sid], [rc]: { ...s.data[sid][rc], ...patch(s.data[sid][rc]) } } },
  }));

  const goStage = (sid) => {
    change((s) => ({ ...s, current: sid }));
    setView("stages");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ── Archivos de evidencia ── */
  const handleUpload = async (sid, rc, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) { alert(`Archivo demasiado grande (máx ${MAX_FILE_MB} MB).`); return; }
    const k = `${sid}:${rc}`;
    setUploading((u) => ({ ...u, [k]: true }));
    try {
      const formData = new FormData();
      formData.append("file", file);
      const result = await ddDd6Api.uploadFile(accessToken, formData);
      updateReq(sid, rc, (r) => ({ files: [...r.files, result.file] }));
    } catch (err) {
      alert(err?.msg || "Error al subir el archivo.");
    } finally {
      setUploading((u) => ({ ...u, [k]: false }));
    }
  };

  const handleDownload = async (file) => {
    const url = await ddDd6Api.getFile(accessToken, file.key);
    if (!url) { alert("No se pudo descargar el archivo."); return; }
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleRemove = (sid, rc, file) => {
    setConfirm({
      open: true,
      msg: `¿Eliminar «${file.name}» del requisito ${rc}? Esta acción no se puede deshacer.`,
      onConfirm: async () => {
        try { await ddDd6Api.deleteFile(accessToken, file.key); } catch { /* archivo huérfano en disco, no es grave */ }
        updateReq(sid, rc, (r) => ({ files: r.files.filter((f) => f.key !== file.key) }));
        setConfirm({ open: false, msg: "", onConfirm: null });
      },
    });
  };

  const exportData = () => {
    const blob = new Blob([JSON.stringify(state.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sustemia-dd6-" + new Date().toISOString().slice(0, 10) + ".json";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="dd6-page"><DDNav /><div className="dd6-loading">Cargando…</div></div>;
  if (loadError) return <DDLoadError className="dd6-page" onRetry={() => setReloadKey((k) => k + 1)} />;

  const stage = DD6_STAGES.find((s) => s.id === state.current) || DD6_STAGES[0];

  return (
    <div className="dd6-page">
      <DDNav />

      <div className="dd6-ph">
        <div>
          <h1 className="dd6-title">Proceso de Debida Diligencia · 6 etapas</h1>
          <span className="dd6-subtitle">El proceso completo de la Guía OCDE. Documentá qué tenés implementado en cada etapa.</span>
        </div>
        <div className="dd6-ph-r">
          <button className="dd6-btn" onClick={exportData}>⇩ Exportar JSON</button>
          {view === "stages"
            ? <button className="dd6-btn" onClick={() => setView("summary")}>Ver resumen →</button>
            : <button className="dd6-btn" onClick={() => setView("stages")}>← Volver a las etapas</button>}
        </div>
      </div>

      {view === "stages" ? (
        <>
          <Stepper data={state.data} current={stage.id} onGo={goStage} />
          <StageView
            stage={stage}
            data={state.data}
            uploading={uploading}
            onStatus={(rc, val) => updateReq(stage.id, rc, () => ({ status: val }))}
            onEvidence={(rc, val) => updateReq(stage.id, rc, () => ({ evidence: val }))}
            onUpload={(rc, e) => handleUpload(stage.id, rc, e)}
            onDownload={handleDownload}
            onRemove={(rc, f) => handleRemove(stage.id, rc, f)}
            onGo={goStage}
            onSummary={() => setView("summary")}
            onModule={(path) => navigate(path)}
          />
        </>
      ) : (
        <SummaryView data={state.data} onGo={goStage} />
      )}

      <ConfirmModal
        open={confirm.open}
        message={confirm.msg}
        danger
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm({ open: false, msg: "", onConfirm: null })}
      />
    </div>
  );
}

/* ─────────────────────────── Stepper ─────────────────────────── */

function Stepper({ data, current, onGo }) {
  return (
    <div className="dd6-steps">
      {DD6_STAGES.map((st) => {
        const sc = dd6StageScore(st, data);
        const done = sc.answered === sc.total && sc.pct >= 100;
        const cls = st.id === current ? "active" : done ? "done" : sc.answered > 0 ? "partial" : "";
        return (
          <button key={st.id} className={`dd6-step ${cls}`} onClick={() => onGo(st.id)} title={st.title}>
            <span className="dd6-step-n">{done ? "✓" : st.n}</span>
            <span className="dd6-step-tx">{st.title}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ─────────────────────────── Etapa ─────────────────────────── */

function StageView({ stage, data, uploading, onStatus, onEvidence, onUpload, onDownload, onRemove, onGo, onSummary, onModule }) {
  const sc = dd6StageScore(stage, data);
  const idx = DD6_STAGES.findIndex((s) => s.id === stage.id);
  const prev = idx > 0 ? DD6_STAGES[idx - 1] : null;
  const next = idx < DD6_STAGES.length - 1 ? DD6_STAGES[idx + 1] : null;

  return (
    <div className="dd6-wrap">
      <div className="dd6-stage-head" style={{ borderLeftColor: stage.color }}>
        <div className="dd6-sh-top">
          <span className="dd6-sh-num" style={{ background: stage.color }}>{stage.n}</span>
          <div className="dd6-sh-main">
            <h2>{stage.title}</h2>
            <div className="dd6-sh-ref">{stage.code} · Guía OCDE 2021, {stage.page}</div>
          </div>
          <div className="dd6-sh-score">
            <div className="v" style={{ color: stage.color }}>{sc.pct}%</div>
            <div className="l">{sc.answered}/{sc.total} requisitos</div>
          </div>
        </div>
        <p className="dd6-sh-intro">{stage.intro}</p>
      </div>

      {stage.linksTo && (
        <div className="dd6-link-box">
          <span className="ic">🔗</span>
          <div><b>Esta etapa ya está cubierta por otro módulo.</b><br />{stage.linksTo.label}.</div>
          <div className="dd6-link-btns">
            {stage.linksTo.modules.map((m) => (
              <button key={m} className="dd6-btn" onClick={() => onModule(DD6_MODULE_LINKS[m].path)}>
                {DD6_MODULE_LINKS[m].label} →
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="dd6-reqs">
        {stage.reqs.map((r) => {
          const d = data[stage.id][r.c];
          const busy = !!uploading[`${stage.id}:${r.c}`];
          return (
            <div key={r.c} className="dd6-req">
              <div className="dd6-req-head">
                <span className="dd6-req-c">{r.c}</span>
                <div>
                  <div className="dd6-req-t">{r.t}</div>
                  <div className="dd6-req-b">{r.b}</div>
                </div>
              </div>

              <div className="dd6-req-controls">
              <div className="dd6-status-grid">
                {DD6_STATE_ORDER.map((k) => {
                  const s = DD6_STATES[k];
                  const sel = d.status === k;
                  return (
                    <button
                      key={k}
                      className={`dd6-st-btn${sel ? " sel" : ""}`}
                      style={sel ? { background: s.bg, color: s.fg, borderColor: s.fg } : undefined}
                      onClick={() => onStatus(r.c, sel ? "pending" : k)}
                      title={sel ? "Volver a «Sin evaluar»" : undefined}
                    >
                      <span>{s.ico}</span>{s.lbl}
                    </button>
                  );
                })}
              </div>

              <div className="dd6-fld">
                <label>Evidencia / cómo lo cumple <span className="opt">(opcional)</span></label>
                <textarea
                  value={d.evidence}
                  placeholder="Política firmada, procedimiento, registro, link publicado, acta de capacitación…"
                  onChange={(e) => onEvidence(r.c, e.target.value)}
                />
              </div>

              <div className="dd6-files">
                {d.files.map((f) => (
                  <div key={f.key} className="dd6-file">
                    <span className="dd6-file-name">📄 {f.name}</span>
                    <span className="dd6-file-meta">{Math.round((f.size || 0) / 1024)} KB · {f.uploadedAt || "—"}</span>
                    <button className="dd6-file-btn" onClick={() => onDownload(f)}>📥 Descargar</button>
                    <button className="dd6-file-btn dd6-file-btn--del" onClick={() => onRemove(r.c, f)}>✕</button>
                  </div>
                ))}
                <label className="dd6-file-btn dd6-file-btn--up">
                  {busy ? "Subiendo…" : "📤 Adjuntar archivo"}
                  <input type="file" accept=".pdf,.docx,.doc,.xlsx,.xls,.jpg,.jpeg,.png" style={{ display: "none" }} disabled={busy} onChange={(e) => onUpload(r.c, e)} />
                </label>
              </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="dd6-nav">
        <div>{prev && <button className="dd6-btn" onClick={() => onGo(prev.id)}>← Etapa {prev.n}</button>}</div>
        <div>
          {next
            ? <button className="dd6-btn dd6-btn--pr" onClick={() => onGo(next.id)}>Etapa {next.n}: {next.title} →</button>
            : <button className="dd6-btn dd6-btn--gr" onClick={onSummary}>Ver resumen del proceso →</button>}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Resumen ─────────────────────────── */

function SummaryView({ data, onGo }) {
  const g = dd6GlobalScore(data);
  const gCls = g.avgPct >= 80 ? "g" : g.avgPct >= 50 ? "y" : "r";

  return (
    <div className="dd6-wrap">
      <div className="dd6-kpis">
        <div className={`dd6-kpi ${gCls}`}>
          <div className="lbl">Avance global del proceso DD</div>
          <div className="vl">{g.avgPct}%</div>
          <div className="meta">promedio de las {g.total} etapas</div>
        </div>
        <div className="dd6-kpi">
          <div className="lbl">Etapas completas</div>
          <div className="vl">{g.stagesDone}/{g.total}</div>
          <div className="meta">todos los requisitos evaluados</div>
        </div>
      </div>

      <h4 className="dd6-h4">Estado por etapa · proceso DD OCDE</h4>
      <table className="dd6-table">
        <thead>
          <tr><th>Etapa</th><th className="center">Requisitos</th><th className="center">Avance</th><th className="center">Estado</th></tr>
        </thead>
        <tbody>
          {DD6_STAGES.map((st) => {
            const sc = dd6StageScore(st, data);
            const lvl = dd6Level(sc);
            return (
              <tr key={st.id} onClick={() => onGo(st.id)}>
                <td>
                  <span className="dd6-tnum" style={{ background: st.color }}>{st.n}</span>
                  {st.title}
                  <span className="dd6-tref">{st.code} · {st.page}</span>
                </td>
                <td className="center">{sc.answered}/{sc.total}</td>
                <td className="center"><span className={`dd6-pct ${lvl.key}`}>{sc.pct}%</span></td>
                <td className="center"><span className={`pill ${lvl.key}`}>{lvl.lbl}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="dd6-disclaim">
        <b>Fuente:</b> OECD Due Diligence Guidance for Responsible Business Conduct — proceso de debida diligencia de 6 etapas
        (Secciones I.1 a I.6). La debida diligencia es un proceso continuo y cíclico, no lineal: las etapas se revisan
        periódicamente para adaptarse a contextos y riesgos cambiantes.
      </div>
    </div>
  );
}
